import React, {
  createContext,
  useContext,
  useLayoutEffect,
} from "react";
import "./App.css";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

import QRPage from "./pages/QRPage";
import NewChatPage from "./pages/NewChatPage";
import BuyTransferableNumberPage from "./pages/BuyTransferableNumberPage";
import Home from "./pages/Home";
import SessionExpiredPage from "./pages/SessionExpiredPage";
import NoActiveChatsPage from "./pages/NoActiveChatsPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import { v4 as uuidv4 } from "uuid";
import SessionBanner from "./components/SessionBanner";
import ChatTransferred from "./pages/ChatTransferred";
import ImportUserFallbackPage from "./pages/ImportUserFallbackPage";
import { useSessionExpiry } from "./utils/useSessionExpiry";
import {
  SessionBannerMenuProvider,
  useSessionBannerMenu,
} from "./context/SessionBannerMenuContext";


const SessionContext = createContext<string | null>(null);

// Custom hook
export const useSessionId = () => {
  const sessionId = useContext(SessionContext);
  if (!sessionId) {
    throw new Error("useSessionId must be used within SessionProvider");
  }
  return sessionId;
};

function SessionBannerWithMenu() {
  const { menu } = useSessionBannerMenu();
  return <SessionBanner menu={menu} />;
}

function App() {
  // ✅ generate ONCE per app mount
  const sessionId = React.useMemo(() => uuidv4(), []);
  useLayoutEffect(() => {
    const setViewportVars = () => {
      const vv = window.visualViewport;

      const height = vv?.height ?? window.innerHeight;
      const offsetTop = vv?.offsetTop ?? 0;

      document.documentElement.style.setProperty("--app-height", `${height}px`);
      document.documentElement.style.setProperty(
        "--visual-viewport-offset-top",
        `${offsetTop}px`,
      );

      const root = document.getElementById("root");
      if (root) {
        root.style.height = `${height}px`;
        root.style.maxHeight = `${height}px`;
        root.style.overflow = "hidden";
      }

      document.body.style.height = `${height}px`;
      document.body.style.maxHeight = `${height}px`;
      document.body.style.overflow = "hidden";
    };

    const sync = () => {
      setViewportVars();
      requestAnimationFrame(setViewportVars);
      setTimeout(setViewportVars, 300);
    };

    sync();

    window.addEventListener("resize", sync);
    window.addEventListener("orientationchange", sync);
    window.visualViewport?.addEventListener("resize", sync);
    window.visualViewport?.addEventListener("scroll", sync);

    return () => {
      window.removeEventListener("resize", sync);
      window.removeEventListener("orientationchange", sync);
      window.visualViewport?.removeEventListener("resize", sync);
      window.visualViewport?.removeEventListener("scroll", sync);
    };
  }, []);
  
  function SessionExpiryWatcher() {
  useSessionExpiry();
  return null;
}

return (
  <SessionContext.Provider value={sessionId}>
    <SessionBannerMenuProvider>
      <div className="App">
        <Router>
          <SessionBannerWithMenu />
          <SessionExpiryWatcher />

          <Routes>
            <Route path="/home" element={<Home />} />
            <Route path="/qr/:username" element={<QRPage />} />

            <Route
              path="/connect/:chatNumber"
              element={<NewChatPage mode="chatNumber" />}
            />
            <Route
              path="/publicid/connect/:publicId"
              element={<NewChatPage mode="publicId" />}
            />

            <Route path="/buynumber" element={<BuyTransferableNumberPage />} />

            <Route
              path="/c/:chatNumber"
              element={<NewChatPage mode="chatNumber" />}
            />
            <Route
              path="/p/:publicId"
              element={<NewChatPage mode="publicId" />}
            />

            <Route path="/session-expired" element={<SessionExpiredPage />} />
            <Route path="/no-active-chats" element={<NoActiveChatsPage />} />
            <Route path="/transferred-chat" element={<ChatTransferred />} />
            <Route path="/importuser" element={<ImportUserFallbackPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          </Routes>
        </Router>
      </div>
    </SessionBannerMenuProvider>
  </SessionContext.Provider>
);
}

export default App;

// Red:
// Green:
// Blue:
// Alpha:
// Radius:
// 139
// 195
// 74
// 255
// 1
